import { useState, useEffect, useRef } from "react";
import { Link, useLocation, Outlet, useNavigate } from "react-router-dom";
import { FaUser, FaSignOutAlt, FaChevronDown, FaBell, FaBuilding, FaQuestionCircle, FaCreditCard, FaTachometerAlt, FaUsers, FaTimes, FaSeedling, FaList, FaFolderOpen, FaWarehouse, FaChartLine, FaDollarSign, FaCalendarAlt } from "react-icons/fa";
import { useUser } from "../contexts/UserContext";
import { useTranslation } from "react-i18next";
import AdminAccountSelector from "../components/AdminAccountSelector";
import CorporateFarmSelector from "../components/CorporateFarmSelector";
import SubUserFarmSelector from "../components/SubUserFarmSelector";
import LanguageSwitcher from "../components/LanguageSwitcher";
import NotificationBell from "../components/NotificationBell";
import FloatingChatWidget from "../components/FloatingChatWidget";
import MobileTopNav from "../components/MobileTopNav";
import MobileBottomTabBar from "../components/MobileBottomTabBar";
import RationIcon from "../components/icons/RationIcon";
import AiAssistantNavButton from "../components/AiAssistantNavButton";
import logo from "../assets/logo.png";
import { isLanguageEnabled } from "../config/languages";


// Navigation constants will be defined inside component to use translations

export default function UserLayout() {
  const { i18n, t } = useTranslation('common');
  const location = useLocation();
  const navigate = useNavigate();
  const { 
    user, 
    logout, 
    selectedFarm, 
    setSelectedFarm, 
    selectedAdminAccount,
    setSelectedAdminAccount,
    selectedCorporateFarm,
    setSelectedCorporateFarm,
    isAdmin, 
    isConsultant,
    isFarmer,
    isCorporateManager,
    isCorporateMember,
    isMultiFarmUser,
    isAdminViewingCorporate,
  } = useUser();
  
  // Set user's preferred language ONLY on initial mount (not on every user change)
  // This respects localStorage and LanguageSwitcher updates
  useEffect(() => {
    const storedLanguage = localStorage.getItem('i18nextLng');
    const storedBase = storedLanguage?.split('-')[0];
    if (storedBase && !isLanguageEnabled(storedBase)) {
      void i18n.changeLanguage('en');
      localStorage.setItem('i18nextLng', 'en');
      return;
    }
    if (!storedLanguage && user?.preferredLanguage && isLanguageEnabled(user.preferredLanguage) && user.preferredLanguage !== i18n.language) {
      // Only set from user preference if no localStorage preference exists
      i18n.changeLanguage(user.preferredLanguage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  // Set HTML direction and language based on current locale
  useEffect(() => {
    document.documentElement.dir = i18n.language === 'fa' ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  // Convert all numbers to Eastern Arabic numerals when language is Farsi
  // Use useLayoutEffect to run synchronously after DOM updates but before paint
  useEffect(() => {
    if (i18n.language === 'fa') {
      let observer: MutationObserver | null = null;
      let timeoutId: NodeJS.Timeout | null = null;
      let intervalId: NodeJS.Timeout | null = null;
      
      import('../utils/numberFormatterGlobal').then(({ convertNumbersToEasternArabic }) => {
        // Initial conversion after React has rendered
        const runConversion = () => {
          convertNumbersToEasternArabic(document.body);
        };
        
        // Run immediately using requestAnimationFrame to catch React's render
        requestAnimationFrame(() => {
          runConversion();
          // Run multiple times to catch all renders
          setTimeout(runConversion, 0);
          setTimeout(runConversion, 50);
          setTimeout(runConversion, 100);
          setTimeout(runConversion, 200);
          setTimeout(runConversion, 500);
        });
        
        // Also run periodically to catch any missed numbers (less frequent to avoid performance issues)
        intervalId = setInterval(runConversion, 3000);
        
        // Set up a MutationObserver to convert numbers in dynamically added content
        observer = new MutationObserver((mutations) => {
          // Use requestAnimationFrame to run after React's render
          requestAnimationFrame(() => {
            // Debounce to avoid too many conversions
            if (timeoutId) clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
              mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                  if (node.nodeType === Node.ELEMENT_NODE || node.nodeType === Node.TEXT_NODE) {
                    const element = node.nodeType === Node.ELEMENT_NODE 
                      ? node as HTMLElement 
                      : (node.parentElement || document.body);
                    convertNumbersToEasternArabic(element);
                  }
                });
              });
              // Also do a full document conversion after mutations
              convertNumbersToEasternArabic(document.body);
            }, 100);
          });
        });
        
        observer.observe(document.body, {
          childList: true,
          subtree: true,
          characterData: true, // Watch for text content changes
        });
      });
      
      // Cleanup observer on unmount or language change
      return () => {
        if (observer) {
          observer.disconnect();
        }
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        if (intervalId) {
          clearInterval(intervalId);
        }
      };
    }
  }, [i18n.language]);

  // Navigation links with translations - must be inside component to use t()
  const adminTopNavLinks = [
    { to: "/admin", label: t('nav.admin'), icon: FaTachometerAlt },
    { to: "/admin/users", label: t('nav.userManagement'), icon: FaUsers },
    { to: "/admin/subscriptions", label: t('nav.subscriptionManagement'), icon: FaCreditCard },
    { to: "/currency-rates", label: 'Currency Rates', icon: FaDollarSign },
  ];

  // Sub-user navigation - simple portal with dashboard + help
  // Dashboard link and label changes based on whether they're multi-farm or single-farm
  const subUserDashboardLink = isMultiFarmUser 
    ? "/multi-farm-dashboard"  // Multi-farm: go to overview
    : "/dashboard";             // Single-farm: go to farm dashboard
    
  const subUserDashboardLabel = isMultiFarmUser
    ? t('nav.allFarms')        // Multi-farm: "All Farms" is clearer
    : t('nav.dashboard');       // Single-farm: "Dashboard"
    
  const consultantTopNavLinks = [
    { to: subUserDashboardLink, label: subUserDashboardLabel, icon: FaTachometerAlt },
    { to: "/help", label: t('nav.help'), icon: FaQuestionCircle },
  ];

  const feedUserNavLinks = [
    { to: "/dashboard", label: t('nav.dashboard'), icon: FaTachometerAlt },
    { to: "/rations", label: t('nav.rations'), icon: RationIcon },
    { to: "/feeding-calendar", label: t('nav.feedingCalendar'), icon: FaCalendarAlt },
    { to: "/inventory", label: t('nav.inventory'), icon: FaWarehouse },
    { to: "/feed-list", label: t('nav.feedList'), icon: FaList },
    { to: "/file-center", label: t('nav.fileCenter'), icon: FaFolderOpen },
    { to: "/records-and-charts", label: t('nav.recordsAndCharts'), icon: FaChartLine },
    { to: "/corporate-dashboard", label: t('nav.corporateDashboard'), icon: FaBuilding, roleRequired: 'CORPORATE' },
    { to: "/help", label: t('nav.help'), icon: FaQuestionCircle },
  ];

  const farmNavLinks = [
    { to: "/dashboard", label: t('nav.dashboard'), icon: FaTachometerAlt },
    { to: "/rations", label: t('nav.rations'), icon: RationIcon },
    { to: "/feeding-calendar", label: t('nav.feedingCalendar'), icon: FaCalendarAlt },
    { to: "/inventory", label: t('nav.inventory'), icon: FaWarehouse },
    { to: "/feed-list", label: t('nav.feedList'), icon: FaList },
    { to: "/file-center", label: t('nav.fileCenter'), icon: FaFolderOpen },
    { to: "/records-and-charts", label: t('nav.recordsAndCharts'), icon: FaChartLine },
  ];

  const corporateTopNavLinks = [
    { to: "/corporate-dashboard", label: t('nav.dashboard'), icon: FaTachometerAlt },
    { to: "/farm-management", label: t('nav.farms'), icon: FaSeedling },
    { to: isCorporateManager || isAdminViewingCorporate ? "/corporate/users" : "/corporate/team", label: t('nav.users'), icon: FaUsers },
    { to: "/corporate/feed-library", label: t('nav.library'), icon: FaList },
    { to: "/help", label: t('nav.support'), icon: FaQuestionCircle },
  ];
  
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [showMobileUserMenu, setShowMobileUserMenu] = useState(false);
  const [aiChatOpen, setAiChatOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const mobileUserMenuRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // Corporate user check
  // Only true CORPORATE role users or SUB_USERs with corporate access (not INDIVIDUAL farm owners under corporate billing)
  const isCorporate = user?.role === 'CORPORATE' || isCorporateManager || isCorporateMember;



  // Close dropdowns when pressing Escape
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowUserMenu(false);
        setShowMobileSidebar(false);
        setShowMobileUserMenu(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
      if (mobileUserMenuRef.current && !mobileUserMenuRef.current.contains(event.target as Node)) {
        setShowMobileUserMenu(false);
      }
      if (sidebarRef.current && !sidebarRef.current.contains(event.target as Node)) {
        setShowMobileSidebar(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Close mobile sidebar on route change
  useEffect(() => {
    setShowMobileSidebar(false);
  }, [location.pathname]);

  const getPageTitleParts = () => {
    const path = location.pathname;
    let pageTitle = '';

    if (path === '/admin') pageTitle = t('nav.admin');
    else if (path === '/admin-users') pageTitle = t('nav.userManagement');
    else if (path === '/admin-subscriptions') pageTitle = t('nav.subscriptionManagement');
    else if (path === '/admin-feed-data') pageTitle = t('nav.globalFeedLibrary');
    else if (path === '/admin-feed-records') pageTitle = t('nav.globalFeedLibrary');
    else if (path === '/dashboard' || path === '/farm-dashboard') pageTitle = t('nav.dashboard');
    else if (path === '/rations') pageTitle = t('nav.rations');
    else if (path === '/feeding-calendar') pageTitle = t('nav.feedingCalendar');
    else if (path === '/feed-list') pageTitle = t('nav.feedList');
    else if (path.startsWith('/feed-list/')) pageTitle = t('inventory:editFeedItem', { defaultValue: 'Edit Feed' });
    else if (path === '/file-center') pageTitle = t('nav.fileCenter');
    else if (path === '/team') pageTitle = t('nav.teamManagement');
    else if (path === '/corporate-dashboard') pageTitle = t('nav.dashboard');
    else if (path === '/farm-management') pageTitle = t('nav.farms');
    else if (path === '/corporate/users' || path === '/corporate/team') pageTitle = t('nav.users');
    else if (path === '/corporate/feed-library') pageTitle = t('nav.library');
    else if (path === '/corporate/billing') pageTitle = t('nav.billing');
    else if (path === '/settings') pageTitle = t('nav.settings');
    else if (path === '/account') pageTitle = t('nav.myAccount');
    else if (path === '/help') pageTitle = t('nav.help');
    else if (path === '/notifications') pageTitle = t('nav.notifications');
    else if (path === '/inventory') pageTitle = t('nav.inventory');
    else if (path === '/records-and-charts') pageTitle = t('nav.recordsAndCharts');
    else pageTitle = 'Feed Cowdemy';

    let farmName = '';
    if (isAdmin || isConsultant) {
      if (selectedAdminAccount) {
        farmName = selectedAdminAccount.type === 'corporate'
          ? selectedAdminAccount.companyName
          : selectedAdminAccount.accountName;
      } else {
        farmName = selectedFarm?.farmName || '';
      }
    } else if (isCorporateManager || isCorporateMember || (user?.corporateAccountId && selectedCorporateFarm)) {
      farmName = selectedCorporateFarm?.farm?.name || '';
    } else {
      farmName = user?.farmName || '';
    }

    return { pageTitle, farmName };
  };

  const getCurrentPageTitle = () => {
    const { pageTitle, farmName } = getPageTitleParts();
    return farmName ? `${farmName} - ${pageTitle}` : pageTitle;
  };

  const getUserInitials = (name?: string) => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0][0]?.toUpperCase() || '?';
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  };

  const { pageTitle: mobilePageTitle, farmName: mobileFarmName } = getPageTitleParts();

  return (
    <div className="app-compact h-screen bg-gray-50 flex flex-col overflow-x-hidden">
      {/* Mobile Menu Overlay */}
      {showMobileSidebar && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setShowMobileSidebar(false)}
        />
      )}

      {/* Main Layout Container */}
      <div className="flex-1 flex relative w-full">
        {/* Sidebar - Responsive */}
        <div className={`${showMobileSidebar ? 'translate-x-0' : i18n.language === 'fa' ? 'translate-x-full' : '-translate-x-full'} lg:translate-x-0 fixed ${i18n.language === 'fa' ? 'right-0' : 'left-0'} w-full lg:w-64 bg-white ${i18n.language === 'fa' ? 'border-l' : 'border-r'} border-gray-200 shadow-sm overflow-y-auto z-50 transition-transform duration-300 ease-in-out h-full`} ref={sidebarRef}>
          {/* Logo and Mobile Close Button */}
          <div className="p-3 border-b border-gray-200 relative">
            {/* Mobile Close Button */}
            <button
              onClick={() => setShowMobileSidebar(false)}
              className={`lg:hidden absolute top-2 ${i18n.language === 'fa' ? 'left-2' : 'right-2'} p-1.5 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100`}
              aria-label="Close menu"
            >
              <FaTimes className="h-5 w-5" />
            </button>
            
            <div className={`flex items-center justify-center ${i18n.language === 'fa' ? 'pl-8 lg:pl-0' : 'pr-8 lg:pr-0'}`}>
              <img 
                src={logo} 
                alt="Feed Cowdemy Logo" 
                className="h-16 lg:h-20 w-auto object-contain"
              />
            </div>
          </div>

          {/* Account Selection (Admin only) */}
          {isAdmin && (
            <div className="border-b border-gray-200 p-3">
              <AdminAccountSelector
                selectedAccount={selectedAdminAccount}
                onAccountSelect={setSelectedAdminAccount}
              />
            </div>
          )}

          {/* Farm Selection (Corporate users) */}
          {isCorporate && (
            <div className="p-4 border-b border-gray-200">
              <CorporateFarmSelector
                selectedFarm={selectedCorporateFarm}
                onFarmSelect={setSelectedCorporateFarm}
              />
              
              {/* Selected Farm Display */}
              {selectedCorporateFarm && (
                <div className="mt-3 p-2 bg-green-50 rounded-md border border-green-200">
                  <div className="text-sm">
                    <div className="font-semibold text-green-900">{selectedCorporateFarm.farm?.name}</div>
                    <div className="text-green-700">{selectedCorporateFarm.accountName}</div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Farm Selection (Sub-users with multiple farms) */}
          {/* Sub-user farm selection - ONLY for multi-farm users */}
          {isConsultant && isMultiFarmUser && (
            <div className="p-4 border-b border-gray-200">
              <SubUserFarmSelector
                selectedFarm={selectedFarm}
                onFarmSelect={setSelectedFarm}
              />
              
              {/* Selected Farm Display */}
              {selectedFarm && (
                <div className="mt-3 p-2 bg-green-50 rounded-md border border-green-200">
                  <div className="text-sm">
                    <div className="font-semibold text-green-900">{selectedFarm.farmName}</div>
                    <div className="text-green-700">{selectedFarm.accountName}</div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Navigation Menu */}
          <nav className="flex-1 py-4">
            {(isFarmer || (isConsultant && !isMultiFarmUser && !isCorporateMember)) ? (
              // Farm users AND single-farm sub-users (but NOT corporate employees) get the full proper navigation - 2 column on mobile
              <div>
                {user?.farmName && (
                  <h3 className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {user.farmName}
                  </h3>
                )}
                {/* Mobile: 3-column grid (full width), Desktop: single column */}
                <div className="grid grid-cols-3 lg:grid-cols-1 gap-2 px-4 lg:px-0">
                  {feedUserNavLinks.map((link) => {
                    // Filter by role if roleRequired is specified
                    if (link.roleRequired && user?.role !== link.roleRequired) return null;
                    
                    const isActive = location.pathname === link.to;
                    const IconComponent = link.icon;
                    return (
                      <Link
                        key={link.to}
                        to={link.to}
                        className={`flex flex-col lg:flex-row items-center lg:gap-3 px-3 lg:px-6 py-3 lg:py-3 text-xs lg:text-sm font-medium transition-colors duration-150 rounded-lg lg:rounded-none ${
                          isActive 
                            ? 'bg-green-50 text-green-700 lg:border-r-2 lg:border-green-500' 
                            : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                        }`}
                      >
                        <IconComponent className="h-4 w-4 mb-1 lg:mb-0" />
                        <span className="text-center lg:text-left leading-tight">{link.label}</span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            ) : isCorporate ? (
              // Corporate navigation - same structure as Admin
              <div>
                {selectedCorporateFarm ? (
                  // Corporate user with selected farm - show farm-specific links FIRST
                  <div className="mb-4">
                    <h3 className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      {t('nav.farmManagement')}
                    </h3>
                    {/* Mobile: 3-column grid (full width), Desktop: single column */}
                    <div className="grid grid-cols-3 lg:grid-cols-1 gap-2 px-4 lg:px-0">
                      {farmNavLinks.map((link) => {
                        const isActive = location.pathname === link.to;
                        const IconComponent = link.icon;
                        return (
                          <Link
                            key={link.to}
                            to={link.to}
                            className={`flex flex-col lg:flex-row items-center lg:gap-3 px-3 lg:px-6 py-3 lg:py-3 text-xs lg:text-sm font-medium transition-colors duration-150 rounded-lg lg:rounded-none ${
                              isActive 
                                ? 'bg-green-50 text-green-700 lg:border-r-2 lg:border-green-500' 
                                : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                            }`}
                          >
                            <IconComponent className="h-4 w-4 mb-1 lg:mb-0" />
                            <span className="text-center lg:text-left leading-tight">{link.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                ) : null}

                {/* Corporate Panel Navigation - Always visible */}
                <div>
                  <h3 className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider border-t border-gray-200 pt-4">
                    {t('nav.corporatePanel')}
                  </h3>
                  {/* Mobile: 3-column grid (full width), Desktop: single column */}
                  <div className="grid grid-cols-3 lg:grid-cols-1 gap-2 px-4 lg:px-0">
                    {corporateTopNavLinks.map((link) => {
                      const isActive = location.pathname === link.to;
                      const IconComponent = link.icon;
                      return (
                        <Link
                          key={link.to}
                          to={link.to}
                          className={`flex flex-col lg:flex-row items-center lg:gap-3 px-3 lg:px-6 py-3 lg:py-3 text-xs lg:text-sm font-medium transition-colors duration-150 rounded-lg lg:rounded-none ${
                            isActive 
                              ? 'bg-green-50 text-green-700 lg:border-r-2 lg:border-green-500' 
                              : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                          }`}
                        >
                          <IconComponent className="h-4 w-4 mb-1 lg:mb-0" />
                          <span className="text-center lg:text-left leading-tight">{link.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                </div>

                {!selectedCorporateFarm && (
                  <div className="px-6 py-4 text-center text-gray-500 text-sm border-t border-gray-200 mt-4 pt-4">
                    <FaBuilding className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                    <p>{t('prompts.selectFarmToView')}</p>
                    <p>{t('prompts.farmSpecificOptions')}</p>
                  </div>
                )}
              </div>
            ) : isAdmin ? (
              // Admin navigation
              <div>
                {selectedAdminAccount && selectedAdminAccount.type !== 'corporate' ? (
                  // Admin with selected farm account - show farm-specific links FIRST
                  <div className="mb-4">
                    <h3 className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Account Management
                    </h3>
                    {/* Mobile: 3-column grid (full width), Desktop: single column */}
                    <div className="grid grid-cols-3 lg:grid-cols-1 gap-2 px-4 lg:px-0">
                      {farmNavLinks.map((link) => {
                        const isActive = location.pathname === link.to;
                        const IconComponent = link.icon;
                        return (
                          <Link
                            key={link.to}
                            to={link.to}
                            className={`flex flex-col lg:flex-row items-center lg:gap-3 px-3 lg:px-6 py-3 lg:py-3 text-xs lg:text-sm font-medium transition-colors duration-150 rounded-lg lg:rounded-none ${
                              isActive 
                                ? 'bg-green-50 text-green-700 lg:border-r-2 lg:border-green-500' 
                                : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                            }`}
                          >
                            <IconComponent className="h-4 w-4 mb-1 lg:mb-0" />
                            <span className="text-center lg:text-left leading-tight">{link.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                ) : null}

                {isAdminViewingCorporate && (
                  <div>
                    <h3 className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider border-t border-gray-200 pt-4">
                      {t('nav.corporatePanel')}
                    </h3>
                    <div className="grid grid-cols-3 lg:grid-cols-1 gap-2 px-4 lg:px-0">
                      {corporateTopNavLinks.map((link) => {
                        const isActive = location.pathname === link.to;
                        const IconComponent = link.icon;
                        return (
                          <Link
                            key={link.to}
                            to={link.to}
                            className={`flex flex-col lg:flex-row items-center lg:gap-3 px-3 lg:px-6 py-3 lg:py-3 text-xs lg:text-sm font-medium transition-colors duration-150 rounded-lg lg:rounded-none ${
                              isActive
                                ? 'bg-green-50 text-green-700 lg:border-r-2 lg:border-green-500'
                                : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                            }`}
                          >
                            <IconComponent className="h-4 w-4 mb-1 lg:mb-0" />
                            <span className="text-center lg:text-left leading-tight">{link.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Admin Panel Navigation */}
                <div>
                  <h3 className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider border-t border-gray-200 pt-4">
                    Admin Panel
                  </h3>
                  {/* Mobile: 3-column grid (full width), Desktop: single column */}
                  <div className="grid grid-cols-3 lg:grid-cols-1 gap-2 px-4 lg:px-0">
                    {adminTopNavLinks.map((link) => {
                      const isActive = location.pathname === link.to;
                      const IconComponent = link.icon;
                      return (
                        <Link
                          key={link.to}
                          to={link.to}
                          className={`flex flex-col lg:flex-row items-center lg:gap-3 px-3 lg:px-6 py-3 lg:py-3 text-xs lg:text-sm font-medium transition-colors duration-150 rounded-lg lg:rounded-none ${
                            isActive 
                              ? 'bg-green-50 text-green-700 lg:border-r-2 lg:border-green-500' 
                              : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                          }`}
                        >
                          <IconComponent className="h-4 w-4 mb-1 lg:mb-0" />
                          <span className="text-center lg:text-left leading-tight">{link.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                </div>

                {!selectedAdminAccount && (
                  // Admin without selected account - show selection prompt
                  <div className="px-6 py-4 text-center text-gray-500 text-sm border-t border-gray-200 mt-4 pt-4">
                    <FaBuilding className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                    <p>{t('prompts.selectAccountToView')}</p>
                    <p>{t('prompts.accountSpecificOptions')}</p>
                  </div>
                )}
              </div>
            ) : isMultiFarmUser ? (
              // Multi-farm sub-user navigation - Simple portal
              <div>
                {selectedFarm ? (
                  // Multi-farm sub-user with selected farm - show farm-specific links FIRST
                  <div className="mb-4">
                    <h3 className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      {t('nav.farmManagement')}
                    </h3>
                    {/* Mobile: 3-column grid (full width), Desktop: single column */}
                    <div className="grid grid-cols-3 lg:grid-cols-1 gap-2 px-4 lg:px-0">
                      {farmNavLinks.map((link) => {
                        const isActive = location.pathname === link.to;
                        const IconComponent = link.icon;
                        return (
                          <Link
                            key={link.to}
                            to={link.to}
                            className={`flex flex-col lg:flex-row items-center lg:gap-3 px-3 lg:px-6 py-3 lg:py-3 text-xs lg:text-sm font-medium transition-colors duration-150 rounded-lg lg:rounded-none ${
                              isActive 
                                ? 'bg-green-50 text-green-700 lg:border-r-2 lg:border-green-500' 
                                : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                            }`}
                          >
                            <IconComponent className="h-4 w-4 mb-1 lg:mb-0" />
                            <span className="text-center lg:text-left leading-tight">{link.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                ) : null}

                {/* Sub-User Portal Navigation - Only All Farms + Help */}
                <div>
                  <h3 className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider border-t border-gray-200 pt-4">
                    Sub-User Portal
                  </h3>
                  {/* Mobile: 3-column grid (full width), Desktop: single column */}
                  <div className="grid grid-cols-3 lg:grid-cols-1 gap-2 px-4 lg:px-0">
                    {consultantTopNavLinks.map((link) => {
                      const isActive = location.pathname === link.to;
                      const IconComponent = link.icon;
                      return (
                        <Link
                          key={link.to}
                          to={link.to}
                          className={`flex flex-col lg:flex-row items-center lg:gap-3 px-3 lg:px-6 py-3 lg:py-3 text-xs lg:text-sm font-medium transition-colors duration-150 rounded-lg lg:rounded-none ${
                            isActive 
                              ? 'bg-green-50 text-green-700 lg:border-r-2 lg:border-green-500' 
                              : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                          }`}
                        >
                          <IconComponent className="h-4 w-4 mb-1 lg:mb-0" />
                          <span className="text-center lg:text-left leading-tight">{link.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                </div>

                {!selectedFarm && (
                  // Multi-farm sub-user without selected farm - show helpful prompt
                  <div className="px-6 py-4 text-center text-gray-500 text-sm border-t border-gray-200 mt-4 pt-4">
                    <FaBuilding className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                    <p>{t('prompts.selectFarmToView')}</p>
                    <p>{t('prompts.farmSpecificOptions')}</p>
                  </div>
                )}
              </div>
            ) : null}
          </nav>
        </div>

        {/* Main Content Area - Responsive */}
        <div className={`flex-1 flex flex-col w-full min-w-0 ${i18n.language === 'fa' ? 'lg:mr-64' : 'lg:ml-64'}`}>
          {/* Header - Responsive */}
          <header className={`fixed top-0 right-0 left-0 ${i18n.language === 'fa' ? 'lg:right-64' : 'lg:left-64'} z-30 lg:bg-white lg:border-b lg:border-gray-200 lg:shadow-sm`}>
            <MobileTopNav
              pageTitle={mobilePageTitle}
              farmName={mobileFarmName || undefined}
              userName={user?.name}
              userEmail={user?.email}
              userType={user?.userType}
              userInitials={getUserInitials(user?.name)}
              isFarmer={isFarmer}
              isRtl={i18n.language === 'fa'}
              aiChatOpen={aiChatOpen}
              onAiChatToggle={() => setAiChatOpen((v) => !v)}
              onOpenSidebar={() => setShowMobileSidebar(true)}
              showUserMenu={showMobileUserMenu}
              onToggleUserMenu={() => setShowMobileUserMenu((v) => !v)}
              onCloseUserMenu={() => setShowMobileUserMenu(false)}
              onCloseSidebar={() => setShowMobileSidebar(false)}
              onLogout={handleLogout}
              userMenuRef={mobileUserMenuRef}
            />
            {/* Desktop Header */}
                         <div className="hidden lg:flex items-center justify-between px-6 py-3">
              {/* Left side: Page Title */}
                             <div className="flex items-center space-x-8">
                 <h1 className="text-xl font-semibold text-gray-900">
                   {getCurrentPageTitle().split(' - ').map((part, index) => {
                     // First part is farm name, second part is page title
                     if (index === 0 && part !== getCurrentPageTitle()) {
                       return <span key={index} className="text-green-600">{part}</span>;
                     } else if (index === 1) {
                       return <span key={index}> - {part}</span>;
                     } else {
                       return <span key={index}>{part}</span>;
                     }
                   })}
                 </h1>
              </div>

              {/* Right side: Language + Notifications + User Menu */}
              <div className="flex items-center space-x-4">
                {/* Language Switcher */}
                <LanguageSwitcher />

                <AiAssistantNavButton
                  isOpen={aiChatOpen}
                  onClick={() => setAiChatOpen((v) => !v)}
                />

                {/* Notifications */}
                <NotificationBell />

                {/* User Menu */}
                <div className="relative" ref={userMenuRef}>
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center space-x-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                  >
                    <FaUser className="h-4 w-4" />
                    <span className="text-sm font-medium hidden md:inline">
                      {user?.name || user?.email}
                    </span>
                    <FaChevronDown className="h-3 w-3" />
                  </button>
                  
                  {showUserMenu && (
                    <div className={`absolute ${i18n.language === 'fa' ? 'left-0' : 'right-0'} mt-2 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-[60]`}>
                      <div className="py-1">
                        <div className="px-4 py-2 text-sm border-b border-gray-100">
                          <div className="font-medium text-gray-900">{user?.name}</div>
                          <div className="text-gray-500">{user?.email}</div>
                          <div className="text-xs text-green-600 capitalize">{user?.userType}</div>
                        </div>
                        <Link
                          to="/account"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowUserMenu(false);
                            navigate("/account");
                          }}
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        >
                          <FaUser className="mr-2 h-4 w-4" />
                          {t('nav.myAccount')}
                        </Link>
                        <Link
                          to="/notification-settings"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowUserMenu(false);
                            navigate("/notification-settings");
                          }}
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        >
                          <FaBell className="mr-2 h-4 w-4" />
                          {t('nav.notificationSettings')}
                        </Link>
                        {isFarmer && (
                          <>
                            <Link
                              to="/team"
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowUserMenu(false);
                                navigate("/team");
                              }}
                              className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            >
                              <FaUsers className="mr-2 h-4 w-4" />
                              {t('nav.teamMembers')}
                            </Link>
                            <Link
                              to="/account#billing"
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowUserMenu(false);
                                navigate("/account#billing");
                              }}
                              className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            >
                              <FaCreditCard className="mr-2 h-4 w-4" />
                              {t('nav.subscriptionAndBilling')}
                            </Link>
                          </>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowUserMenu(false);
                            handleLogout();
                          }}
                          className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 border-t border-gray-100"
                        >
                          <FaSignOutAlt className="mr-2 h-4 w-4" />
                          {t('nav.logout')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </header>

          {/* Page Content - Responsive Padding for Header + bottom tab bar on mobile */}
          <div className="flex-1 overflow-auto bg-[#F2F2F7] lg:bg-gray-50 w-full pt-11 lg:pt-16 pb-[72px] lg:pb-0">
            <Outlet />
          </div>
        </div>
      </div>

      {/* Mobile bottom tab bar */}
      <MobileBottomTabBar
        isFarmer={isFarmer}
        isCorporate={isCorporate || isAdminViewingCorporate}
        hasSelectedFarm={isCorporate ? !!selectedCorporateFarm : !!(selectedAdminAccount && selectedAdminAccount.type !== 'corporate')}
        isAdmin={isAdmin}
        isConsultant={isConsultant}
        onMorePress={() => setShowMobileSidebar(true)}
      />

      {/* Footer — desktop only */}
      <footer className="hidden lg:block bg-white border-t border-gray-200 py-3">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-sm text-gray-500">
            <p>{t('common:copyright', { year: new Date().getFullYear() })}</p>
            <p className="text-xs text-gray-400 mt-0.5">{t('common:copyrightProduct')}</p>
          </div>
        </div>
      </footer>

      {/* AI Assistant Chat Widget */}
      <FloatingChatWidget isOpen={aiChatOpen} onOpenChange={setAiChatOpen} />
    </div>
  );
}
